/**
 * Swiper slider for Best Selling section (categories + products).
 * Mirrors product-slider behaviour with optional autoplay + seamless loop.
 */
if (!customElements.get('best-selling-slider')) {
  customElements.define(
    'best-selling-slider',
    class BestSellingSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.selectors = {
          productsWrap: '.products-grid-wrap',
          products: '.products-grid',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
          pagination: '.swiper-pagination',
        };
        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.sectionId = this.dataset.sectionId;
        this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);

        this.enableSlider = this.dataset.enableSlider === 'true';
        this.items = parseInt(this.dataset.items, 10);
        this.tabletItems = parseInt(this.dataset.tabletItems, 10);
        this.totalItems = parseInt(this.dataset.totalItems, 10);
        this.paginationType = this.dataset.paginationType || 'progressbar';
        this.autoplayDelay = parseInt(this.dataset.autoplay, 10) || 0;
        this.pauseOnHover = this.dataset.pauseOnHover === 'true';

        this.sliderInstance = false;

        if (!this.enableSlider) return;
        if (!this.elements.productsWrap || !this.elements.products) return;

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.init.bind(this);

        const mqlTablet = window.matchMedia(FoxTheme.config.mediaQueryTablet);
        mqlTablet.onchange = this.init.bind(this);

        this.init();
      }

      init() {
        if (FoxTheme.config.mqlMobile) {
          this.destroySlider();
          return;
        }

        const currentItems = FoxTheme.config.mqlTablet ? this.tabletItems : this.items;

        if (this.totalItems > currentItems) {
          this.initSlider();
        } else {
          this.destroySlider();
        }
      }

      getSpaceBetween() {
        const columnGap = window
          .getComputedStyle(this.elements.products)
          .getPropertyValue('--f-column-gap')
          .trim();

        if (!columnGap || columnGap.startsWith('var(')) {
          const fallback =
            getComputedStyle(this.elements.products).getPropertyValue('--f-column-gap-lg').trim() ||
            getComputedStyle(this.elements.products).getPropertyValue('--f-column-gap-md').trim() ||
            '1.6rem';
          if (fallback.endsWith('rem')) return parseFloat(fallback) * 10;
          if (fallback.endsWith('px')) return parseFloat(fallback);
          return 16;
        }

        if (columnGap.endsWith('rem')) return parseFloat(columnGap) * 10;
        if (columnGap.endsWith('px')) return parseFloat(columnGap);

        const parsed = parseFloat(columnGap);
        return Number.isFinite(parsed) ? parsed * 10 : 16;
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object') return;

        const spaceBetween = this.getSpaceBetween();
        const enableAutoplay = this.autoplayDelay > 0 && FoxTheme.Swiper.Autoplay;
        const modules = [FoxTheme.Swiper.Mousewheel];

        const sliderOptions = {
          slidesPerView: 2,
          spaceBetween: spaceBetween,
          navigation: {
            nextEl: this.elements.nextEl,
            prevEl: this.elements.prevEl,
          },
          pagination: {
            el: this.elements.pagination,
            type: this.paginationType,
          },
          breakpoints: {
            768: {
              slidesPerView: this.tabletItems,
            },
            1024: {
              slidesPerView: this.items,
            },
          },
          // Seamless restart — no hard jump back to slide 1
          loop: enableAutoplay,
          loopAdditionalSlides: enableAutoplay ? this.items : 0,
          speed: 600,
          threshold: 2,
          watchSlidesProgress: true,
          mousewheel: {
            enabled: true,
            forceToAxis: true,
          },
        };

        if (enableAutoplay) {
          modules.push(FoxTheme.Swiper.Autoplay);
          sliderOptions.autoplay = {
            delay: this.autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: this.pauseOnHover,
          };
        }

        this.sliderOptions = sliderOptions;

        this.elements.productsWrap.classList.add(this.classes.swiper);
        this.elements.products.classList.remove(this.classes.grid);
        this.elements.products.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(
          this.elements.productsWrap,
          this.sliderOptions,
          modules
        );
        this.sliderInstance.init();
        this.handleAccessibility();
        this.fixQuickviewDuplicate();
        this.calcNavButtonsPosition();
      }

      handleAccessibility() {
        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', (event) => {
            if (event.relatedTarget !== null) {
              if (element.closest('.swiper-slide')) {
                const slide = element.closest('.swiper-slide');
                this.sliderInstance.slider.slideTo(
                  this.sliderInstance.slider.slides.indexOf(slide)
                );
              }
            } else {
              element.blur();
            }
          });
        });
      }

      destroySlider() {
        this.elements.productsWrap.classList.remove(this.classes.swiper);
        this.elements.productsWrap.classList.remove('best-selling-slider--continuous');
        this.elements.products.classList.remove(this.classes.swiperWrapper);
        this.elements.products.classList.add(this.classes.grid);
        if (typeof this.sliderInstance !== 'object') return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }

      fixQuickviewDuplicate() {
        const modalIds = [];
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          const modalID = modal.getAttribute('id');
          if (modalIds.includes(modalID)) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }

      calcNavButtonsPosition() {
        if (this.dataset.navigationPosition !== 'center_image') return;

        const firstMedia = this.querySelector(
          '.product-card__image-wrapper, .best-selling__category-media'
        );
        const controls = this.querySelector('.swiper-controls');
        if (controls && firstMedia && firstMedia.clientHeight > 0) {
          controls.style.setProperty(
            '--swiper-navigation-top-offset',
            parseInt(firstMedia.clientHeight, 10) / 2 + 'px'
          );
        }
      }
    }
  );
}
